import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { trpc } from '@/lib/trpc';
import { useQuery } from '@tanstack/react-query';
import type { Plan } from '@deepguide-ai/types';

/**
 * Unified hook for feature flag + plan checks
 *
 * Combines feature flag status with subscription plan context
 * to provide comprehensive access control information.
 *
 * @example
 * ```tsx
 * const { enabled, needsUpgrade, requiredPlan, isKillSwitched } =
 *   useFeatureAccess('analytics');
 *
 * if (isKillSwitched) {
 *   return <ComingSoonBadge />;
 * }
 *
 * if (needsUpgrade) {
 *   return <UpgradePrompt requiredPlan={requiredPlan} />;
 * }
 *
 * return <AnalyticsDashboard />;
 * ```
 *
 * @param enabled - Pass false to skip both underlying queries (e.g. read-only/public
 * views that have no authenticated session and would otherwise 401).
 */
export function useFeatureAccess(featureFlag: string, enabled: boolean = true) {
  const featureFlagResult = useFeatureFlag(featureFlag, enabled);
  const { data: subscription } = useQuery({
    ...trpc.subscription.current.queryOptions(),
    enabled,
  });

  // Get required plan from feature flag metadata
  const requiredPlan = featureFlagResult.metadata?.requiredPlan as Plan | undefined;
  const allPlans = featureFlagResult.metadata?.allPlans as Plan[] | undefined;
  const currentPlan = (featureFlagResult.metadata?.currentPlan as Plan) || subscription?.plan || 'FREE';

  return {
    // Access
    enabled: featureFlagResult.enabled,
    loading: featureFlagResult.loading,

    // Plan context
    currentPlan,
    requiredPlan,
    allPlans,

    // Reason
    reason: featureFlagResult.reason,

    // Metadata
    isKillSwitched: featureFlagResult.metadata?.killSwitch ?? false,
    category: featureFlagResult.metadata?.category,
    prerequisites: (featureFlagResult.metadata?.prerequisites as string[]) ?? [],

    // Trial info
    isTrial: featureFlagResult.metadata?.trial ?? false,
    trialEnd: featureFlagResult.metadata?.trialEnd ?? null,

    // Helper
    needsUpgrade: !featureFlagResult.enabled && !!requiredPlan,
  };
}
